/**
 * RespiratoryCard — iter1051 (Respiratory гл. 42-50)
 * Вентилация и механика, обструкция/рестрикция, газообмен и V/Q,
 * реален пренос на кислород и контрол на дишането.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Loader2, AlertTriangle, Wind, Timer, Waves } from "lucide-react";
import SixMinuteWalkPanel from "./SixMinuteWalkPanel";
import BreathProgramPanel from "./BreathProgramPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const META = {
  suspect: { label: "Слабо място", cls: "bg-rose-500 text-white" },
  watch: { label: "Следи се", cls: "bg-amber-500 text-white" },
  ok: { label: "Работи", cls: "bg-emerald-500 text-white" },
  no_data: { label: "Няма данни", cls: "bg-slate-400 text-white" },
};

const VITAL_FIELDS = [
  ["mmrc", "Задух mMRC (0–4)"],
  ["resp_rate", "Дихателна честота (/мин)"],
  ["breath_hold_sec", "Задържане на дъха (сек)"],
  ["spo2_pct", "SpO₂ в покой (%)"],
  ["spo2_walk_pct", "SpO₂ след ходене (%)"],
  ["fev1_pct", "FEV1 (% от предвиденото)"],
  ["fvc_pct", "FVC (% от предвиденото)"],
  ["pef_best", "Пиков поток — най-добър"],
  ["pef_now", "Пиков поток — днес"],
  ["cigs_per_day", "Цигари на ден"],
  ["smoking_years", "Години пушене"],
  ["altitude_m", "Височина (м)"],
];

export const RespiratoryCard = ({ isDark, onSaved }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openForm, setOpenForm] = useState(false);
  const [open, setOpen] = useState(null);
  const [walkOpen, setWalkOpen] = useState(false);
  const [breathOpen, setBreathOpen] = useState(false);
  const [sel, setSel] = useState([]);
  const [vitals, setVitals] = useState({});

  const apply = (d) => {
    setData(d);
    setSel(d.screen_selected || []);
    setVitals(d.vitals || {});
  };

  useEffect(() => {
    axios.get(`${API}/respiratory/snapshot`, { timeout: 25000 })
      .then(({ data: d }) => apply(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const reload = () => axios.get(`${API}/respiratory/snapshot`, { timeout: 25000 })
    .then(({ data: d }) => apply(d)).catch(() => {});

  const toggle = (k) => setSel((s) => s.includes(k) ? s.filter((x) => x !== k) : [...s, k]);

  const save = async () => {
    setSaving(true);
    try {
      const clean = {};
      Object.entries(vitals).forEach(([k, v]) => {
        if (v !== "" && v !== null && v !== undefined) clean[k] = v;
      });
      const { data: d } = await axios.post(`${API}/respiratory/screen`, { items: sel, vitals: clean });
      apply(d);
      setOpenForm(false);
      onSaved && onSaved(d);
    } finally { setSaving(false); }
  };

  const card = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200";
  if (loading) {
    return <div className={`p-4 rounded-2xl border ${card}`} data-testid="respiratory-loading">
      <Loader2 className="w-4 h-4 animate-spin text-sky-500" />
    </div>;
  }
  if (!data) return null;

  return (
    <div className={`p-4 rounded-2xl border ${card}`} data-testid="respiratory-card">
      <div className="flex items-center gap-2 mb-1">
        <Wind className="w-4 h-4 text-sky-500" />
        <h2 className="font-bold text-sm">Дишане и кислород</h2>
        {data.score != null && (
          <span className="ml-auto text-xs font-extrabold tabular-nums" data-testid="respiratory-score">
            {data.score}<span className="opacity-50 text-[10px]">/100</span>
          </span>
        )}
      </div>
      <p className="text-[11px] opacity-70 mb-3" data-testid="respiratory-summary">{data.summary_bg}</p>

      <div className="grid grid-cols-2 gap-2 mb-3">
        <button onClick={() => { setWalkOpen((v) => !v); setBreathOpen(false); }}
                data-testid="respiratory-walk-btn"
                className="py-2.5 rounded-xl bg-sky-600 text-white text-[11px] font-bold flex items-center justify-center gap-1.5 hover:bg-sky-700 transition-colors">
          <Timer className="w-3.5 h-3.5" /> 6-минутно ходене
        </button>
        <button onClick={() => { setBreathOpen((v) => !v); setWalkOpen(false); }}
                data-testid="respiratory-breath-btn"
                className={`py-2.5 rounded-xl text-[11px] font-bold flex items-center justify-center gap-1.5 transition-colors ${
                  data.breath_program_active
                    ? "bg-emerald-600 text-white hover:bg-emerald-700"
                    : (isDark ? "bg-emerald-500/20 text-emerald-200 hover:bg-emerald-500/30"
                              : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100")
                }`}>
          <Waves className="w-3.5 h-3.5" />
          {data.breath_program_active ? "Програма за дъха (активна)" : "Програма за дъха"}
        </button>
      </div>
      {walkOpen && <SixMinuteWalkPanel isDark={isDark} onSaved={reload} />}
      {breathOpen && <BreathProgramPanel isDark={isDark} onChanged={reload} />}

      {(data.red_flags || []).map((f) => (
        <div key={f.key} data-testid={`respiratory-flag-${f.key}`}
             className={`p-3 rounded-xl mb-2 border-2 ${
               f.urgency === "emergency"
                 ? (isDark ? "bg-rose-900/40 border-rose-500" : "bg-rose-50 border-rose-400")
                 : (isDark ? "bg-amber-900/30 border-amber-500/60" : "bg-amber-50 border-amber-300")
             }`}>
          <div className="flex items-start gap-2">
            <AlertTriangle className={`w-4 h-4 mt-0.5 ${f.urgency === "emergency" ? "text-rose-500" : "text-amber-500"}`} />
            <div className="min-w-0">
              <div className="text-xs font-bold">{f.title_bg}</div>
              <p className="text-[11px] mt-1 leading-relaxed opacity-90">{f.text_bg}</p>
              <div className="text-[11px] mt-1.5 font-semibold">{f.action_bg}</div>
            </div>
          </div>
        </div>
      ))}

      <div className="space-y-2">
        {(data.blocks || []).map((b) => {
          const m = META[b.status] || META.no_data;
          const isOpen = open === b.key;
          return (
            <div key={b.key} data-testid={`respiratory-block-${b.key}`}
                 className={`rounded-xl border ${isDark ? "border-slate-800 bg-slate-800/30" : "border-slate-200 bg-slate-50"}`}>
              <button onClick={() => setOpen(isOpen ? null : b.key)}
                      data-testid={`respiratory-toggle-${b.key}`}
                      className="w-full text-left p-3 flex items-center gap-2">
                <span className="text-[11px] font-bold flex-1 min-w-0">{b.title_bg}</span>
                <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold shrink-0 whitespace-nowrap ${m.cls}`}
                      data-testid={`respiratory-status-${b.key}`}>{m.label}</span>
                <span className="text-[10px] opacity-50">{isOpen ? "−" : "+"}</span>
              </button>
              {isOpen && (
                <div className="px-3 pb-3 space-y-2">
                  <div className="text-[11px] font-semibold">{b.label_bg}</div>
                  <p className="text-[11px] leading-relaxed opacity-90">{b.explain_bg}</p>
                  <p className="text-[10px] italic opacity-75">{b.mechanism_bg}</p>
                  {(b.signals || []).length > 0 && (
                    <ul className="space-y-0.5" data-testid={`respiratory-signals-${b.key}`}>
                      {b.signals.map((s) => <li key={s} className="text-[10px] opacity-80">• {s}</li>)}
                    </ul>
                  )}
                  {b.o2_math && b.o2_math.capacity_ml_dl != null && (
                    <div className={`p-2 rounded-lg text-[10px] ${isDark ? "bg-sky-500/10" : "bg-sky-50"}`}
                         data-testid="respiratory-o2-math">
                      <div className="font-bold mb-0.5">Сметката за Вашата кръв</div>
                      <div>1.34 ml × {(b.o2_math.hb_g_l / 10).toFixed(1)} g хемоглобин = <strong>{b.o2_math.capacity_ml_dl} ml/dl</strong> капацитет</div>
                      <div>× {b.o2_math.sat_used_pct}% сатурация + 0.3 разтворен = <strong>{b.o2_math.content_ml_dl} ml/dl</strong> реално пренесени</div>
                      <div className="opacity-80">Типично: {b.o2_math.reference_ml_dl} ml/dl → Вие сте на <strong>{b.o2_math.pct_of_reference}%</strong></div>
                    </div>
                  )}
                  {(b.curve_shifts || []).length > 0 && (
                    <div className={`p-2 rounded-lg text-[10px] ${isDark ? "bg-slate-900/60" : "bg-white border border-slate-200"}`}
                         data-testid="respiratory-curve-shifts">
                      <div className="font-bold opacity-80 mb-1">Изместване на кривата при Вас</div>
                      {b.curve_shifts.map((s) => (
                        <div key={s.title_bg} className="mb-1">
                          <strong>{s.title_bg}</strong> ({s.direction}) — {s.text_bg}
                        </div>
                      ))}
                    </div>
                  )}
                  {b.hypoxia_type && (
                    <div className={`p-2 rounded-lg text-[10px] ${isDark ? "bg-amber-500/10" : "bg-amber-50"}`}
                         data-testid="respiratory-hypoxia-type">
                      <strong>{b.hypoxia_type.label_bg}</strong> — {b.hypoxia_type.text_bg}
                    </div>
                  )}
                  {(b.patterns || []).length > 0 && (
                    <div className={`p-2 rounded-lg text-[10px] ${isDark ? "bg-slate-900/60" : "bg-white border border-slate-200"}`}
                         data-testid="respiratory-patterns">
                      <div className="font-bold opacity-80 mb-1">Модели на дишане при Вас</div>
                      {b.patterns.map((p) => (
                        <div key={p.key} className="mb-1" data-testid={`respiratory-pattern-${p.key}`}>
                          <strong>{p.title_bg}</strong> — {p.text_bg}
                        </div>
                      ))}
                    </div>
                  )}
                  {[["ascent_rules", "Правила за изкачване"], ["dive_rules", "Правила за гмуркане"]].map(([field, title]) => (
                    (b[field] || []).length > 0 && (
                      <div key={field} className={`p-2 rounded-lg text-[10px] ${isDark ? "bg-emerald-500/10" : "bg-emerald-50 border border-emerald-200"}`}
                           data-testid={`respiratory-${field.replace("_", "-")}`}>
                        <div className="font-bold opacity-80 mb-1">{title}</div>
                        {b[field].map((r) => (
                          <div key={r.title_bg} className="mb-1.5">
                            <strong>{r.title_bg}</strong> — <span className="font-semibold">{r.target_bg}</span>
                            <div className="opacity-80">{r.why_bg}</div>
                          </div>
                        ))}
                      </div>
                    )
                  ))}
                  {(b.notes || []).length > 0 && (
                    <div className={`p-2 rounded-lg text-[10px] ${isDark ? "bg-slate-900/60" : "bg-white border border-slate-200"}`}
                         data-testid={`respiratory-notes-${b.key}`}>
                      {b.notes.map((n) => (
                        <div key={n.title_bg} className="mb-1">
                          <strong>{n.title_bg}</strong> — {n.text_bg}
                        </div>
                      ))}
                    </div>
                  )}
                  {(b.missing || []).length > 0 && (
                    <div className={`p-2 rounded-lg text-[10px] ${isDark ? "bg-slate-900/60" : "bg-white border border-slate-200"}`}
                         data-testid={`respiratory-missing-${b.key}`}>
                      <div className="font-bold opacity-70 mb-1">Какво липсва, за да се реши</div>
                      {b.missing.map((x) => (
                        <div key={x.marker} className="mb-0.5"><strong>{x.label_bg}</strong> — {x.why_bg}</div>
                      ))}
                    </div>
                  )}
                  {(b.links || []).map((l, i) => (
                    <button key={`${b.key}-${i}`} onClick={() => navigate(l.route)}
                            data-testid={`respiratory-link-${b.key}-${i}`}
                            className={`w-full text-left p-2 rounded-lg text-[10px] transition-colors ${
                              l.urgency === "high"
                                ? (isDark ? "bg-rose-500/15 hover:bg-rose-500/25" : "bg-rose-50 hover:bg-rose-100")
                                : (isDark ? "bg-sky-500/10 hover:bg-sky-500/20" : "bg-sky-50 hover:bg-sky-100")
                            }`}>
                      <span className="font-bold">{l.title_bg} →</span>
                      <span className="opacity-80"> {l.action_bg}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <button onClick={() => setOpenForm((v) => !v)} data-testid="respiratory-form-toggle"
              className={`mt-3 w-full text-[11px] font-semibold py-2 rounded-lg transition-colors ${
                isDark ? "bg-sky-500/15 text-sky-200 hover:bg-sky-500/25"
                       : "bg-sky-50 text-sky-700 hover:bg-sky-100"
              }`}>
        {openForm ? "Скрий въпросите" : `Дишане: числа и въпроси (${(data.screen_selected || []).length} отбелязани)`}
      </button>

      {openForm && (
        <div className="mt-2 space-y-2" data-testid="respiratory-form">
          <div className={`p-2 rounded-lg text-[10px] ${isDark ? "bg-slate-800/40" : "bg-slate-50 border border-slate-200"}`}
               data-testid="respiratory-mmrc-scale">
            <div className="font-bold opacity-70 mb-1">Скала mMRC — кое описва Вас</div>
            {(data.mmrc_scale || []).map((g) => (
              <div key={g.grade}>{g.grade} — {g.label_bg}</div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2">
            {VITAL_FIELDS.map(([k, lbl]) => (
              <label key={k} className="text-[10px] opacity-80">
                {lbl}
                <input type="number" step="any" value={vitals[k] ?? ""}
                       onChange={(e) => setVitals((d) => ({ ...d, [k]: e.target.value }))}
                       data-testid={`respiratory-input-${k}`}
                       className={`w-full mt-0.5 px-2 py-1 rounded-lg text-[11px] ${
                         isDark ? "bg-slate-900 border border-slate-700" : "bg-white border border-slate-300"
                       }`} />
              </label>
            ))}
          </div>
          <div className="space-y-1.5">
            {(data.screen_items || []).map((it) => (
              <label key={it.key} data-testid={`respiratory-screen-${it.key}`}
                     className={`flex items-start gap-2 p-2 rounded-lg cursor-pointer text-[11px] ${
                       sel.includes(it.key) ? (isDark ? "bg-sky-500/20" : "bg-sky-50")
                                            : (isDark ? "bg-slate-800/40" : "bg-slate-50")
                     }`}>
                <input type="checkbox" checked={sel.includes(it.key)}
                       onChange={() => toggle(it.key)} className="mt-0.5" />
                <span>
                  <span className="opacity-50 text-[9px] uppercase font-bold block">{it.group_bg}</span>
                  {it.label_bg}
                </span>
              </label>
            ))}
          </div>
          <button onClick={save} disabled={saving} data-testid="respiratory-save"
                  className="w-full py-2 rounded-lg bg-sky-600 text-white text-[11px] font-bold disabled:opacity-60">
            {saving ? "Записвам…" : "Запиши"}
          </button>
        </div>
      )}
    </div>
  );
};

export default RespiratoryCard;
